import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoadingState } from '@/components/ui/primitives';
import { useAuth } from '@/hooks/useAuth';
import { DashboardPage } from '@/pages/DashboardPage';
import { DebugPage } from '@/pages/DebugPage';
import { DeviceDetailsPage } from '@/pages/DeviceDetailsPage';
import { DevicesPage } from '@/pages/DevicesPage';
import { HistoryPage } from '@/pages/HistoryPage';
import { LoginPage } from '@/pages/LoginPage';
import { AlertsPage } from '@/pages/AlertsPage';
import { ZonesPage } from '@/pages/ZonesPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { FieldTestsPage } from '@/pages/FieldTestsPage';
import { IndustrializationPage } from '@/pages/IndustrializationPage';
import { MeasurementsPage } from '@/pages/MeasurementsPage';
import { FieldTestDetailPage } from '@/pages/FieldTestDetailPage';
import { PwaStatus } from '@/components/pwa/PwaStatus';
import { NativeRuntime } from '@/components/native/NativeRuntime';

function RequireAuth({ children }: { children: React.ReactNode }): JSX.Element {
  const { session, initializing } = useAuth();
  const location = useLocation();

  if (initializing) return <LoadingState label="Restauration de la session…" />;
  if (!session) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <AppShell>{children}</AppShell>;
}

export function App(): JSX.Element {
  const { session, initializing } = useAuth();

  return (
    <>
    <NativeRuntime />
    <Routes>
      <Route
        path="/login"
        element={
          initializing ? (
            <LoadingState label="Restauration de la session…" />
          ) : session ? (
            <Navigate to="/" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/devices"
        element={
          <RequireAuth>
            <DevicesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/devices/:deviceNum"
        element={
          <RequireAuth>
            <DeviceDetailsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/devices/:deviceNum/history"
        element={
          <RequireAuth>
            <HistoryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/history"
        element={
          <RequireAuth>
            <HistoryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/alerts" element={<RequireAuth><AlertsPage /></RequireAuth>}
      />
      <Route path="/zones" element={<RequireAuth><ZonesPage /></RequireAuth>} />
      <Route path="/reports" element={<RequireAuth><ReportsPage /></RequireAuth>} />
      <Route path="/field-tests" element={<RequireAuth><FieldTestsPage /></RequireAuth>} />
      <Route path="/field-tests/:campaignId" element={<RequireAuth><FieldTestDetailPage /></RequireAuth>} />
      <Route path="/industrialization" element={<RequireAuth><IndustrializationPage /></RequireAuth>} />
      <Route path="/measurements" element={<RequireAuth><MeasurementsPage /></RequireAuth>} />
      <Route
        path="/debug"
        element={
          <RequireAuth>
            <DebugPage />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <PwaStatus />
    </>
  );
}
